import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { UserContextService } from 'src/services/user-context.service';
import { RouteLifecycleService } from '../services/route-lifecycle.service';
import { RoutePermission, RoutePermissionKey } from 'src/app/core/models/route-permission-key';

const VALID_ROUTE_PERMISSION_KEYS = new Set<string>(Object.values(RoutePermission));

/**
 * Enhanced AuthGuard for Angular Router
 *
 * Key Features:
 * - Maintains compatibility with existing UserContextService permission logic
 * - Handles user context loading timing to ensure permissions are available
 * - Validates route permissions before allowing navigation
 */
@Injectable({
    providedIn: 'root'
})
export class EnhancedAuthGuard implements CanActivate {

    private readonly USER_CONTEXT_TIMEOUT = 10000; // 10 second timeout for context loading

    constructor(
        private router: Router,
        private userContextService: UserContextService,
        private routeLifecycleService: RouteLifecycleService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

        // 1. Handle unauthenticated routes first (login, public pages)
        if (this.isUnauthenticatedRoute(route)) {
            return true;
        }

        // 2. Check if user is authenticated at all
        if (!this.isUserAuthenticated()) {
            return this.redirectToLogin(state.url);
        }

        // 3. Critical: Ensure user context is loaded before checking permissions
        return this.ensureUserContextAndCheckPermissions(route, state.url);
    }

    /**
     * The core of solving resolveUserContext timing issues
     * This method ensures user context is fully loaded before any permission checks
     */
    private ensureUserContextAndCheckPermissions(
        route: ActivatedRouteSnapshot,
        targetUrl: string
    ): Observable<boolean | UrlTree> {

        // If context is already loaded, check permissions immediately
        if (this.userContextService.isUserContextLoaded()) {
            const permissionResult = this.checkRoutePermissions(route);

            // If permissions passed, handle route lifecycle (equivalent to onEnter)
            if (permissionResult === true) {
                this.routeLifecycleService.handleRouteEnter(route);
            }

            return of(permissionResult);
        }

        // Context not loaded - load it before checking permissions
        return this.userContextService.loadUserContextAsObservable().pipe(
            timeout(this.USER_CONTEXT_TIMEOUT),
            map(() => {
                // Now that context is loaded, check permissions
                const permissionResult = this.checkRoutePermissions(route);

                // If permissions passed, handle route lifecycle (equivalent to onEnter)
                if (permissionResult === true) {
                    this.routeLifecycleService.handleRouteEnter(route);
                }

                return permissionResult;
            }),
            catchError((error) => {
                // Handle different types of failures
                if (error.name === 'TimeoutError') {
                    // console.error('AuthGuard: User context loading timed out after', this.USER_CONTEXT_TIMEOUT, 'ms');
                    return of(this.router.createUrlTree(['/error'], {
                        queryParams: { type: 'context_timeout' }
                    }));
                }

                // A 401 means the server no longer accepts the session even though the JWT cookie
                // still looks valid client-side (isUserAuthenticated passed to get here). This is the
                // server-side session timeout (~15 min): the token hasn't hit its exp claim, but the
                // backend has dropped the session. Re-authentication is the correct recovery, so send
                // the user to login (preserving the intended route) rather than a dead-end error page.
                if (error?.status === 401) {
                    // console.error('AuthGuard: User context load returned 401 (session expired), redirecting to login');
                    return of(this.redirectToLogin(targetUrl));
                }

                // Other errors (network, 5xx, etc.) are genuine failures, not auth problems.
                // console.error('AuthGuard: User context loading failed:', error);
                return of(this.router.createUrlTree(['/error'], {
                    queryParams: { type: 'context_load_error' }
                }));
            })
        );
    }

    /**
     * Check route permissions using existing UserContextService logic
     * This maintains compatibility with the existing 920-line permission system
     */
    private checkRoutePermissions(
        route: ActivatedRouteSnapshot
    ): boolean | UrlTree {

        const routeName = this.getRouteNameFromRoute(route);
        const requiresPermission = this.getPermissionRequirement(route);

        // If route doesn't require permissions, allow access
        if (!requiresPermission) {
            return true;
        }

        // Angular runs canActivate on EVERY segment of the activated path, so an ancestor
        // route's permission is now checked in addition to the target's. UI-Router only
        // checked the destination state. When an ancestor is stricter than a child the user
        // legitimately has (e.g. `configuration` requires viewFirmUsers but `producerRules`
        // only needs maintainSegment), the user is wrongly bounced to /error.
        //
        // Enforce permission only on the TERMINAL route of the activated chain: if any
        // activated descendant declares its own permission metadata, defer the decision to
        // it. Layout/outlet placeholder children carry no requiresPermission and are ignored,
        // so leaf-with-outlets routes (e.g. filingWorkflow) still enforce their own permission.
        if (this.hasPermissionedDescendant(route)) {
            return true;
        }

        // Use existing UserContextService permission logic - DO NOT REWRITE!
        try {
            // Validate routeName is a known RoutePermission value before calling the permission
            // lookup. getRouteNameFromRoute() has a URL-derived fallback that can produce arbitrary
            // strings; a bare `as RoutePermissionKey` would silently pass them through. Any
            // permission-gated route that reaches this branch should have data.permissionKey set —
            // if it doesn't, the console error below surfaces the misconfiguration at runtime.
            if (!VALID_ROUTE_PERMISSION_KEYS.has(routeName)) {
                // TODO: log this error during https://dj.sircon.com/browse/FOF-7884
                //console.error('AuthGuard: routeName is not a declared RoutePermission key — check data.permissionKey on route', routeName);
                return this.router.createUrlTree(['/error'], {
                    queryParams: { type: 'permission_error', route: routeName }
                });
            }
            const hasPermission = this.userContextService.hasPermissionToVisitRoute(routeName as RoutePermissionKey);

            if (hasPermission) {
                return true;
            } else {
                // Check if user has restricted SSO access
                if (this.isRestrictedSsoUser(routeName)) {
                    return this.router.createUrlTree(['/error'], {
                        queryParams: { type: 'unauthorized_sso', route: routeName }
                    });
                }

                // Redirect to error page with unauthorized type
                return this.router.createUrlTree(['/error'], {
                    queryParams: { type: 'unauthorized', route: routeName }
                });
            }
        } catch {
            //console.error('AuthGuard: Permission check error for route', routeName, error);
            return this.router.createUrlTree(['/error'], {
                queryParams: { type: 'permission_error', route: routeName }
            });
        }
    }

    /**
     * True if any ACTIVATED descendant of this route declares its own requiresPermission
     * metadata (true or false). ActivatedRouteSnapshot.children contains only the routes on
     * the current navigation path, so this distinguishes an ancestor segment (defer to the
     * descendant) from the terminal target (enforce here).
     *
     * IMPORTANT: use `child.routeConfig?.data` (the static config object), NOT `child.data`
     * (the merged snapshot data). `child.data` inherits all ancestor `data` fields via Angular
     * Router's data-merge walk, so every child would appear to declare `requiresPermission` even
     * when only a parent does — making `hasPermissionedDescendant` always return true and
     * bypassing all permission enforcement on ancestor routes.
     */
    private hasPermissionedDescendant(route: ActivatedRouteSnapshot): boolean {
        return route.children.some(child =>
            child.routeConfig?.data?.['requiresPermission'] !== undefined ||
            this.hasPermissionedDescendant(child)
        );
    }

    /**
     * Extract route name with fallbacks for UI-Router compatibility
     */
    private getRouteNameFromRoute(route: ActivatedRouteSnapshot): string {
        // The canonical permission key (data.permissionKey), falling back to a URL-derived name
        // for routes that don't declare one.
        return route.data?.['permissionKey'] ||
            this.buildRouteNameFromPath(route);
    }

    /**
     * Build hierarchical route name from URL path (like UI-Router state names)
     */
    private buildRouteNameFromPath(route: ActivatedRouteSnapshot): string {
        const segments: string[] = [];
        let current: ActivatedRouteSnapshot | null = route;

        while (current) {
            if (current.routeConfig?.path && current.routeConfig.path !== '') {
                segments.unshift(current.routeConfig.path);
            }
            current = current.parent;
        }

        return segments.join('.');
    }

    /**
     * Determine if route requires permissions
     */
    private getPermissionRequirement(route: ActivatedRouteSnapshot): boolean {
        return route.data?.['requiresPermission'] === true;
    }

    /**
     * Check if this is an unauthenticated route (login, public pages)
     */
    private isUnauthenticatedRoute(route: ActivatedRouteSnapshot): boolean {
        return route.data?.['unauthenticated'] === true;
    }

    /**
     * Check if user is authenticated (has valid session)
     */
    private isUserAuthenticated(): boolean {
        try {
            return !!this.userContextService.getUserId();
        } catch {
            // console.error('AuthGuard: Error checking authentication status', error);
            return false;
        }
    }

    /**
     * Check if user has restricted SSO access
     */
    private isRestrictedSsoUser(routeName: string): boolean {
        try {
            return !!(this.userContextService.userContext?.permissions?.restrictedRoutesForCarrierSso &&
                   !this.userContextService.userContext.permissions.restrictedRoutesForCarrierSso.includes(routeName));
        } catch {
            return false;
        }
    }

    /**
     * Redirect to login with return URL
     * Note: LoginComponent expects 'redirectRoute' query param (UI-Router convention)
     */
    private redirectToLogin(returnUrl: string): UrlTree {
        // Strip error query params before redirecting to login
        // e.g., '/dashboard/activity?type=context_load_error' -> '/dashboard/activity'
        const cleanUrl = returnUrl.split('?')[0];

        // Convert Angular Router path to UI-Router state name format for LoginComponent compatibility
        // e.g., '/dashboard/activity' -> 'dashboard.activity'
        const redirectRoute = cleanUrl.substring(1).replace(/\//g, '.');

        // Navigation-only: LoginComponent.convertUiRouterToAngularRoute() maps this dot-notation
        // value back to a slash-path via its own routeMapping table, then router.navigate()s there.
        // That re-triggers EnhancedAuthGuard.canActivate on the real destination, which does the
        // actual camelCase permission check via route.data — redirectRoute itself is never passed
        // to hasPermissionToVisitRoute(). No re-encoding needed here.
        return this.router.createUrlTree(['/login'], {
            queryParams: { redirectRoute }
        });
    }
}
